import { useEffect } from 'react';

import { Button } from '@mui/material';
import Container from '@mui/material/Container';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { getEmployeeById } from 'src/redux/slices/employees';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';

import ErrorContent from 'src/components/error-content';
import { useSettingsContext } from 'src/components/settings';
import { LoadingScreen } from 'src/components/loading-screen';
import { LeftIcon } from 'src/components/carousel/arrow-icons';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';

import AdvancedPaymentNewEditForm from '../advanced-payment-new-edit-form';

type Props = {
  employeeID: string;
};

export default function AdvancedPaymentCreateView({ employeeID }: Props) {
  const { employee, status, error } = useAppSelector((state) => state.employees);

  const dispatch = useAppDispatcher();
  const settings = useSettingsContext();

  useEffect(() => {
    dispatch(getEmployeeById({ employeeId: employeeID }));
  }, [dispatch, employeeID]);

  if (status === 'loading' || status === 'idle') {
    return <LoadingScreen />;
  }

  if (status === 'failure') {
    return (
      <ErrorContent
        filled
        title="Failed to load employee details"
        description={error}
        action={
          <Button
            component={RouterLink}
            href={paths.dashboard.employees.root}
            variant="contained"
            startIcon={<LeftIcon />}
          >
            Back to Employees
          </Button>
        }
        sx={{
          py: 10,
        }}
      />
    );
  }

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Create a new advanced payment"
        links={[
          {
            name: 'Employees',
            href: paths.dashboard.employees.root,
          },
          { name: 'Create a new advanced payment' },
        ]}
        sx={{
          mb: { xs: 3, md: 5 },
        }}
      />

      <AdvancedPaymentNewEditForm employee={employee} />
    </Container>
  );
}
